import { useMutation } from '@apollo/client';
import { createForm } from '@seek/forms-ui';
import type { JobCategoryRef } from '@seek/job-category';
import { useTranslations } from '@vocab/react';
import { Alert, Divider, Stack, Text } from 'braid-design-system';
import React, { useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
  conciergeSubmitEvent,
  conciergeSubmitFailedEvent,
  conciergeSubmitSuccessEvent,
  guaranteedHireSubmitEvent,
  guaranteedHireSubmitFailedEvent,
  guaranteedHireSubmitSuccessEvent,
  premiumPlusSubmitEvent,
  premiumPlusSubmitFailedEvent,
  premiumPlusSubmitSuccessEvent,
} from 'src/app/analytics/concierge';
import { CONCIERGE_WAIT_DAYS } from 'src/app/constants/concierge';
import { COUNTRY } from 'src/app/constants/configObjectProperties';
import {
  PRODUCT_CONCIERGE,
  PRODUCT_GUARANTEED_HIRE,
  PRODUCT_PREMIUM_PLUS,
} from 'src/app/constants/job-ad-booking-products';
import { SECTION_BOOK_A_CALL } from 'src/app/constants/pageSectionName';
import {
  PAGE_ID_CONCIERGE,
  PAGE_ID_GUARANTEED_HIRE,
  PAGE_ID_PREMIUM_PLUS,
} from 'src/app/constants/pageUid';
import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  JOB_AD_BOOKING_FORM_SUBMIT_FAILED,
  JOB_AD_BOOKING_FORM_SUBMIT_PRESSED,
  JOB_AD_BOOKING_FORM_SUBMIT_SUCCEEDED,
} from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { getConfig } from 'src/app/utils/config';
import { addDays } from 'src/app/utils/date';
import handleValidation from 'src/app/utils/handleValidation';
import { log } from 'src/app/utils/logging';
import { withDesktopEnhancers } from 'src/app/views/enhancers/withDesktopEnhancers';
import { useConfig } from 'src/app/views/hooks/useConfig';
import PageLayout from 'src/app/views/layouts/PageLayout';
import { PUBLISH_JOB_AD_BOOKING_EVENT } from 'src/graphql/publishJobAdBookingEvent';
import type AppState from 'src/types/appState';
import type { JobTitleRef, JobLocationRef } from 'src/types/formFields';
import type { CreateFlowPageContainer } from 'src/types/pages';
import type { TealiumEventData } from 'src/types/trackingData';

import translations from './.vocab';
import { FormFields } from './components/FormFields';
import { Intro } from './components/Intro';
import { RecruiterNetworkBanner } from './components/RecruiterNetworkBanner';
import { Success } from './components/Success';

const getFutureDate = (baseDate: Date): string =>
  addDays(baseDate, CONCIERGE_WAIT_DAYS).toISOString();

// NOTE: In some countries on particular products we append an additional label to job title
// In order to explicitly tell customer service agents that this product is different to
// what they would usually expect (ie, in AU it is rare for it to be "Concierge" product)
const JOB_TITLE_SUFFIX: Record<string, Record<string, string>> = {
  [PRODUCT_CONCIERGE]: {
    AU: ' - CONCIERGE BOOKING',
  },
};
const PRODUCT_NAME: Record<string, string> = {
  [PRODUCT_GUARANTEED_HIRE]: 'Guaranteed Hire',
  [PRODUCT_CONCIERGE]: 'Concierge',
  [PRODUCT_PREMIUM_PLUS]: 'Premium Plus',
};
const PRODUCT_ANALYTICS_EVENTS: Record<
  string,
  Record<string, TealiumEventData>
> = {
  [PRODUCT_GUARANTEED_HIRE]: {
    submitEvent: guaranteedHireSubmitEvent,
    submitSuccessEvent: guaranteedHireSubmitSuccessEvent,
    submitFailedEvent: guaranteedHireSubmitFailedEvent,
  },
  [PRODUCT_CONCIERGE]: {
    submitEvent: conciergeSubmitEvent,
    submitSuccessEvent: conciergeSubmitSuccessEvent,
    submitFailedEvent: conciergeSubmitFailedEvent,
  },
  [PRODUCT_PREMIUM_PLUS]: {
    submitEvent: premiumPlusSubmitEvent,
    submitSuccessEvent: premiumPlusSubmitSuccessEvent,
    submitFailedEvent: premiumPlusSubmitFailedEvent,
  },
};

export interface FormValues {
  contactName: string;
  phoneNumber: string;
  userEmail: string;
}

const { FormProvider, useField } = createForm<FormValues>();

const buildPageForProductType = (productType: string) => () => {
  const { getLanguage } = useConfig();
  const { t } = useTranslations(translations);

  const {
    advertiserId,
    companyName,
    contactName,
    hirerNation,
    jobLocation,
    jobTitle,
    phoneNumber,
    tealium,
    userEmail,
    jobLocationCountry,
    jobCategorySubClassificationId,
    jobLocationId,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.seekAdvertiserId,
      companyName: state.session.advertiserName,
      contactName:
        `${state.session.userFirstName} ${state.session.userLastName}`.trimEnd(),
      hirerNation: state.session.hirerAccount.nation,
      jobLocation: state.session.jobLocationText,
      jobTitle: state.formData.jobTitle,
      phoneNumber: state.session.phoneNumber,
      tealium: state.tealium,
      userEmail: state.session.userEmailAddress,
      jobLocationCountry: state.session.jobLocationCountry?.toLocaleUpperCase(),
      jobCategorySubClassificationId: state.session.seekSubClassificationId,
      jobLocationId: state.formData.jobLocationId,
    }),
    shallowEqual,
  );

  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const jobTitleSuffix: string =
    JOB_TITLE_SUFFIX[productType]?.[jobLocationCountry] || '';
  const productName: string = PRODUCT_NAME[productType];
  const { submitEvent, submitSuccessEvent, submitFailedEvent } =
    PRODUCT_ANALYTICS_EVENTS[productType];

  const initialValues = {
    contactName,
    phoneNumber,
    userEmail,
    jobLocation,
  };

  const [jobAdBookingFormValues, setJobAdBookingFormValues] =
    useState<FormValues>(initialValues);

  // TODO: Investigate auto generation of the GraphQL client code.
  const [submitBooking, { data, loading, error }] = useMutation(
    PUBLISH_JOB_AD_BOOKING_EVENT,
    {
      variables: {
        input: {
          advertiserId,
          ...jobAdBookingFormValues,
          companyName,
          contactBy: getFutureDate(new Date()),
          jobLocation,
          jobTitle: `${jobTitle}${jobTitleSuffix}`,
          websiteCountryCode: getConfig(COUNTRY),
          jobCountryCode: jobLocationCountry,
          jobCategorySubClassificationId,
          jobLocationId,
        },
      },
      onCompleted: () => {
        if (isPageAnalyticsEnabled) {
          getTrackingAdapter.sendLinkEvent({
            eventName: JOB_AD_BOOKING_FORM_SUBMIT_SUCCEEDED,
          });
        } else {
          sendLinkEvent(tealium, submitSuccessEvent);
        }
      },
      onError: (err) => {
        log.error('Job Ad Booking | Submission Failed', {
          error: err,
          advertiserId,
          jobLocationCountry,
          productType,
        });
        if (isPageAnalyticsEnabled) {
          getTrackingAdapter.sendLinkEvent({
            eventName: JOB_AD_BOOKING_FORM_SUBMIT_FAILED,
          });
        } else {
          sendLinkEvent(tealium, submitFailedEvent);
        }
      },
    },
  );

  const submitState = data?.publishJobAdBookingEvent?.published;
  const didSucceed = submitState === true;
  const didError = submitState === false || error;

  const jobTitleRef = useRef<JobTitleRef>(null);
  const jobLocationRef = useRef<JobLocationRef>(null);
  const jobCategoryRef = useRef<JobCategoryRef>(null);

  const containerComponentMap: CreateFlowPageContainer[] = [
    {
      containerId: SECTION_BOOK_A_CALL,
      components: [jobTitleRef, jobLocationRef, jobCategoryRef],
    },
  ];

  const handleComponentValidation = (skipIfNull = false) => {
    const validationResult = handleValidation(
      containerComponentMap,
      skipIfNull,
    );

    return validationResult;
  };

  const shouldShowRNBanner =
    (productType === PRODUCT_CONCIERGE ||
      productType === PRODUCT_GUARANTEED_HIRE) &&
    hirerNation === 'AU';

  return (
    <PageLayout
      hasAside
      hasProgressBar={false}
      handleValidation={handleComponentValidation}
    >
      <Stack space="large">
        {didSucceed ? (
          <Success
            contactName={jobAdBookingFormValues.contactName}
            phoneNumber={jobAdBookingFormValues.phoneNumber}
            waitDays={CONCIERGE_WAIT_DAYS}
          />
        ) : (
          <>
            {shouldShowRNBanner && (
              <>
                <RecruiterNetworkBanner />
                <Divider />
              </>
            )}
            <Intro productName={productName} waitDays={CONCIERGE_WAIT_DAYS} />
            <FormProvider
              initialValues={initialValues}
              language={getLanguage()}
            >
              {({ handleSubmit }) => {
                const onSubmit = handleSubmit((formValues: FormValues) => {
                  const validationErrors: string[] =
                    handleComponentValidation();
                  if (validationErrors.length === 0) {
                    if (isPageAnalyticsEnabled) {
                      getTrackingAdapter.sendLinkEvent({
                        eventName: JOB_AD_BOOKING_FORM_SUBMIT_PRESSED,
                      });
                    } else {
                      sendLinkEvent(tealium, submitEvent);
                    }
                    setJobAdBookingFormValues(formValues);
                    submitBooking();
                  }
                });
                return (
                  <FormFields
                    onSubmit={onSubmit}
                    useField={useField}
                    isLoading={loading}
                    productType={productType}
                    jobTitleRef={jobTitleRef}
                    jobLocationRef={jobLocationRef}
                    jobCategoryRef={jobCategoryRef}
                  />
                );
              }}
            </FormProvider>
            {didError && (
              <Alert tone="critical">
                <Text>{t('job-ad-booking-error-message')}</Text>
              </Alert>
            )}
          </>
        )}
      </Stack>
    </PageLayout>
  );
};

export const Concierge = buildPageForProductType(PRODUCT_CONCIERGE);
export const GuaranteedHire = buildPageForProductType(PRODUCT_GUARANTEED_HIRE);
export const PremiumPlus = buildPageForProductType(PRODUCT_PREMIUM_PLUS);

export const ConciergePage = withDesktopEnhancers(PAGE_ID_CONCIERGE)(Concierge);
export const GuaranteedHirePage = withDesktopEnhancers(PAGE_ID_GUARANTEED_HIRE)(
  GuaranteedHire,
);
export const PremiumPlusPage =
  withDesktopEnhancers(PAGE_ID_PREMIUM_PLUS)(PremiumPlus);
